import React, { useCallback, useMemo } from 'react';
import { AdaptiveFilter, Box, CalendarPickerIcon, DateRangeDropdown, OptionKey, useResponsive } from '@common-fe/common-fe';

import { ALL_KEY } from '@/common/constants';

import { useGetAccountFundsQuery } from '../queries';

import TransactionsAmountSelect from './TransactionsAmountSelect';
import TransactionsFundsSelect from './TransactionsFundsSelect';

const INIT_PAGE = 1;

interface Props {
  onSetPage: (value: number) => void;

  currentAmount: OptionKey;
  onSetCurrentAmount: (value: OptionKey) => void;

  selectedFunds: OptionKey[];
  onSelectFunds: (values: OptionKey[]) => void;

  dateRange: Array<Date | null>;
  onSetDateRange: (value: Array<Date | null>) => void;
}
const TransactionsHistoryFilters: React.FC<Props> = ({
  onSetPage,
  dateRange,
  onSetDateRange,
  selectedFunds,
  onSelectFunds,
  currentAmount,
  onSetCurrentAmount,
}) => {
  const { isLoading: isLoadingFunds, data: fundOptions } = useGetAccountFundsQuery();
  const { isMobile } = useResponsive();

  const {
    isFilterVisible,
    setIsFilterVisible,
    handleCancelFilter,
    handleSubmitFilter,
    handleResetFilter,
    filters,
    setFilter,
  } = AdaptiveFilter.useAdaptiveFilter<{
    currentAmount: OptionKey;
    selectedFunds: OptionKey[];
    dateRange: Array<Date | null>;
  }>({
    initialFilters: {
      selectedFunds: selectedFunds || [],
      currentAmount: currentAmount || ALL_KEY,
      dateRange: dateRange || [null, null],
    },

    onSubmit: (appliedFilters) => {
      onSetDateRange(appliedFilters.dateRange);
      onSetCurrentAmount(appliedFilters.currentAmount);
      onSelectFunds(appliedFilters.selectedFunds);
    },
  });
  const isFilterApplied = useMemo(() => {
    return !!selectedFunds.length || !!currentAmount || !!dateRange?.[0] || !!dateRange?.[1];
  }, [currentAmount, dateRange, selectedFunds.length]);

  const handleClear = useCallback(() => {
    setIsFilterVisible(false);
    handleResetFilter();
    onSetDateRange([null, null]);
    onSetCurrentAmount('');
    onSelectFunds([]);
  }, [handleResetFilter, onSelectFunds, onSetCurrentAmount, onSetDateRange, setIsFilterVisible]);
  if (isMobile) {
    return (
      <AdaptiveFilter
        isFilterApplied={isFilterApplied}
        testId="test-filter"
        title="Title"
        onClear={handleClear}
        isVisible={isFilterVisible}
        onSetIsVisible={setIsFilterVisible}
        footer={
          <AdaptiveFilter.Footer
            testId="footer"
            onCancel={handleCancelFilter}
            onSubmit={handleSubmitFilter}
          />
        }
      >
        <div>
          <AdaptiveFilter.ChipsToggler
            testId="status"
            label="Status"
            options={fundOptions}
            values={filters.selectedFunds}
            onChangeValues={(value) => {
              setFilter('selectedFunds', value);
            }}
          />
        </div>
        <AdaptiveFilter.DateRange
          isDateRangeMode
          testId="period"
          label="Period"
          value={filters.dateRange as [Date | null, Date | null]}
          onChangeValue={(value) => setFilter('dateRange', value)}
        />
      </AdaptiveFilter>
    );
  }
  return (
    <Box direction="row">
      <Box width="180px">
        <TransactionsFundsSelect
          isLoading={isLoadingFunds}
          options={fundOptions}
          values={selectedFunds}
          onChangeValues={onSelectFunds}
        />
      </Box>
      <Box width="180px" margin={{ left: 's' }}>
        <TransactionsAmountSelect value={currentAmount} onChangeValue={onSetCurrentAmount} />
      </Box>
      <Box width="180px" margin={{ left: 's' }}>
        <DateRangeDropdown
          prefix="Period:"
          testId="transaction-history_period"
          onChange={(range) => {
            onSetDateRange(range);
            onSetPage(INIT_PAGE);
          }}
          ellipsisMode
          startDate={dateRange[0]}
          endDate={dateRange[1]}
          icon={
            <Box>
              <CalendarPickerIcon color="iconPrimary" />
            </Box>
          }
        />
      </Box>
    </Box>
  );
};
export default TransactionsHistoryFilters;
